import React, { useState } from 'react';
import { createRecoveryPoint, Environment, RecoveryPointType } from '../api';
import { Callout, Dialog, OptionCard, OptionGroup, Select } from './primitives';
import { Field } from './ui';
import { useIdempotencyKey } from '../idempotency';
import { actionLabel } from './operation/actionLabels';

interface CreateBackupModalProps {
  projectId: string;
  /** The environment to snapshot; the server comes from its placement. */
  environment: Environment;
  onClose: () => void;
  onCreated: (recoveryPointId: string, executionId: string) => void;
}

export const CreateBackupModal: React.FC<CreateBackupModalProps> = ({
  projectId,
  environment,
  onClose,
  onCreated,
}) => {
  const [includeMedia, setIncludeMedia] = useState<boolean>(true);
  const [compression, setCompression] = useState<string>('zstd');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const resp = await createRecoveryPoint(
        projectId,
        {
          environmentId: environment.id,
          type: 'manual' as RecoveryPointType,
          includeMedia,
          compression,
        },
        idempotency.key(),
      );
      idempotency.settle();
      onCreated(resp.recovery_point.id, resp.execution.id);
    } catch (err: any) {
      idempotency.settle(err);
      setError(err.message || 'The server gave no reason.');
      setLoading(false);
    }
  };

  return (
    <Dialog
      open
      size="md"
      title={`Back up ${environment.name}`}
      onClose={onClose}
      busy={loading}
      onSubmit={handleSubmit}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={loading}>
            {loading ? 'Starting the backup' : error ? actionLabel.retry : 'Back up now'}
          </button>
        </>
      }
    >
      {error ? (
        <Callout tone="danger" title="The backup did not start">
          <p>
            <strong>Reported error:</strong> {error}
          </p>
          <p>Nothing was backed up.</p>
        </Callout>
      ) : null}

      <OptionGroup label="What to back up">
        <OptionCard
          title="Database and media"
          description={
            <>
              The database dump and the <code>pub/media</code> archive.
            </>
          }
          selected={includeMedia}
          selectedLabel="Selected"
          onSelect={() => setIncludeMedia(true)}
        />
        <OptionCard
          title="Database only"
          description="Faster; media files are not included."
          selected={!includeMedia}
          selectedLabel="Selected"
          onSelect={() => setIncludeMedia(false)}
        />
      </OptionGroup>

      <Field label="Compression">
        <Select value={compression} onChange={(e) => setCompression(e.target.value)}>
          <option value="zstd">Zstandard (smaller, fast)</option>
          <option value="gzip">Gzip (most compatible)</option>
        </Select>
      </Field>

    </Dialog>
  );
};
